import React, { FC, useMemo, useState } from 'react';
import { Collapse, Modal } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import SeparatorWithText from '../common/SeperatorWithText';
import { getBuyerSpecificAssetIdOrElseAssetId } from '../../utils/assetUtils';
import {
	getProtectedImageUriForBuyer,
	getProtectedImageUriForSupplier,
} from '../../utils/FileAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import moment from 'moment';

const { Panel } = Collapse;

interface ScannedAssetDataSectionProps {
	assetAudit: any;
	userType: string;
}

const ScannedAssetDataSection: FC<ScannedAssetDataSectionProps> = ({ assetAudit, userType }) => {
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const asset = useMemo(() => nullSafeGetOrElse('asset', assetAudit, {}), [assetAudit]);

	const imageUris = useMemo(() => {
		const images = nullSafeGetOrElse('images', asset, []);
		return images.map((img) => {
			const [fileId, fileName] = img.split('/');
			return userType === ROLE_TYPES.SUPPLIER
				? getProtectedImageUriForSupplier(fileId, fileName, 200, 200)
				: getProtectedImageUriForBuyer(fileId, fileName, 200, 200);
		});
	}, [asset, userType]);

	const fullImageUris = useMemo(() => {
		const images = nullSafeGetOrElse('images', asset, []);
		return images.map((img) => {
			const [fileId, fileName] = img.split('/');
			return userType === ROLE_TYPES.SUPPLIER
				? getProtectedImageUriForSupplier(fileId, fileName, 800, 600)
				: getProtectedImageUriForBuyer(fileId, fileName, 800, 600);
		});
	}, [asset, userType]);

	const details = useMemo(
		() => [
			{
				label: 'Asset ID:',
				value: getBuyerSpecificAssetIdOrElseAssetId(asset),
			},
			{
				label: 'Asset No:',
				value: nullSafeGetOrElse('assetNumber', asset, '--'),
			},
			{
				label: 'Serial No:',
				value: nullSafeGetOrElse('serialNumber', asset, '--'),
			},
			{
				label: 'Model No:',
				value: nullSafeGetOrElse('modelNumber', asset, '--'),
			},
			{
				label: 'Manufacturer:',
				value: nullSafeGetOrElse('manufacturer', asset, '--'),
			},
			{
				label: 'Purchase Date:',
				value: nullSafeGet('purchaseDate', asset)
					? moment(asset.purchaseDate).format('YYYY-MM-DD')
					: '--',
			},
			{
				label: 'Installation Date:',
				value: nullSafeGet('installDate', asset)
					? moment(asset.installDate).format('YYYY-MM-DD')
					: '--',
			},
		],
		[asset]
	);

	return (
		<>
			<Collapse
				defaultActiveKey={['scanned-data']}
				ghost
				expandIconPosition="right"
				style={{ margin: '0 -16px', paddingTop: 0 }}
				className="scanned-data-collapse"
			>
				<Panel header={<span className="h5medium">Scanned Data:</span>} key="scanned-data">
					<SeparatorWithText className="-mt-2 mb-3" />
					{imageUris.length > 0 ? (
						<div className="mb-3 flex flex-row gap-2">
							{imageUris.map((uri, idx) => (
								<div
									key={idx}
									className="cursor-pointer overflow-hidden rounded"
									style={{ width: 80, height: 80 }}
									onClick={() => {
										setPreviewImage(fullImageUris[idx]);
										setPreviewVisible(true);
									}}
								>
									<img
										src={uri}
										alt={`Asset ${idx + 1}`}
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								</div>
							))}
						</div>
					) : null}
					<div className="flex flex-col gap-1">
						{details.map((detail) => (
							<div
								key={detail.label}
								className="flex flex-row items-center gap-2 text-character-primary"
							>
								<span className="bodyregular" style={{ minWidth: 120 }}>
									{detail.label}
								</span>
								<span className="bodymedium">{detail.value}</span>
							</div>
						))}
					</div>
				</Panel>
			</Collapse>
			<Modal visible={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
				<img alt="preview" style={{ width: '100%' }} src={previewImage} />
			</Modal>
		</>
	);
};

export default ScannedAssetDataSection;
